import { UPLOAD_MAX_RETRIES, UPLOAD_RETRY_DELAYS_MS } from './constants';
import { uploadChunkToS3 } from './s3-upload';
import type { RecordingAdapter, UploadPartInfo } from './types';

/**
 * Upload an already-recorded audio file through the SAME multipart lifecycle
 * the live recorder uses (start → PUT parts → complete).
 *
 * WHY THIS IS NOT A SECOND UPLOAD PATH. The backend contract — presigned parts,
 * ETag collection, `complete` kicking off Sarvam STT and the SAMVAAD summary —
 * is identical whether the bytes came from a microphone or from disk. The only
 * real difference is that a file's bytes all exist up front, so there is no
 * IndexedDB spooling, no auto-stop clock and no worklet. Everything downstream
 * of `complete` cannot tell the two apart, which is the point: a meeting that
 * was recorded on a phone gets the same summary as one recorded in the tab.
 */

/** Audio container types the pipeline's STT step accepts. */
export const ACCEPTED_UPLOAD_MIME_TYPES = [
  'audio/mpeg',
  'audio/mp4',
  'audio/m4a',
  'audio/x-m4a',
  'audio/wav',
  'audio/x-wav',
  'audio/webm',
  'audio/ogg',
  'audio/aac',
  'audio/flac',
] as const;

/** The `accept` attribute for the file input — extensions included because
 *  browsers report inconsistent MIME types for m4a and aac on some platforms. */
export const UPLOAD_ACCEPT_ATTR =
  '.mp3,.m4a,.wav,.webm,.ogg,.aac,.flac,.mp4,audio/*';

/**
 * 500 MB. Comfortably past a 3-hour m4a at speech bitrates, while still
 * refusing the pasted-in video file that would burn a long upload and then fail
 * transcription anyway.
 */
export const MAX_UPLOAD_BYTES = 500 * 1024 * 1024;

export interface FileUploadProgress {
  /** Parts fully uploaded so far. */
  uploadedParts: number;
  /** Total parts this file will be split into. */
  totalParts: number;
  /** 0–100, whole percent. */
  percent: number;
}

export interface UploadRecordingFileResult {
  job_id: number | null;
  recording_status: string;
  mom_generated: boolean;
}

/** Human-readable reason the file was refused, or null when it is acceptable. */
export function validateRecordingFile(file: File): string | null {
  if (file.size === 0) return 'That file is empty.';
  if (file.size > MAX_UPLOAD_BYTES) {
    const mb = Math.round(MAX_UPLOAD_BYTES / (1024 * 1024));
    return `That file is larger than ${mb} MB. Please upload the audio track only.`;
  }
  // Type check is advisory: some browsers report '' for .m4a picked from disk,
  // and refusing on an empty type would block a valid recording. Only a
  // positively-identified non-audio type is rejected.
  if (file.type && !file.type.startsWith('audio/') && file.type !== 'video/mp4') {
    return 'That is not an audio file. Upload the meeting audio (mp3, m4a, wav, webm).';
  }
  return null;
}

/**
 * How long to wait for container metadata before giving up and reporting 0.
 *
 * A codec the browser can decode answers in milliseconds. One it cannot may
 * fire NEITHER `loadedmetadata` nor `error` — so without this ceiling the
 * upload never starts and the button sits there looking broken.
 */
const DURATION_PROBE_TIMEOUT_MS = 5000;

/**
 * Best-effort duration in whole seconds, read from the container metadata.
 *
 * Returns 0 when the browser cannot decode the header. `duration_seconds` is
 * reporting metadata on the recording row, not something the pipeline branches
 * on, so a failure here must not fail the upload — the transcript is derived
 * from the audio itself.
 */
export function readAudioDurationSeconds(file: File): Promise<number> {
  return new Promise((resolve) => {
    let settled = false;
    let url: string | null = null;

    const done = (seconds: number) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      if (url) URL.revokeObjectURL(url);
      resolve(Number.isFinite(seconds) && seconds > 0 ? Math.round(seconds) : 0);
    };

    const timer = setTimeout(() => done(0), DURATION_PROBE_TIMEOUT_MS);

    try {
      url = URL.createObjectURL(file);
      const audio = new Audio();
      audio.preload = 'metadata';
      audio.onloadedmetadata = () => done(audio.duration);
      audio.onerror = () => done(0);
      audio.src = url;
    } catch {
      // No Audio or no createObjectURL (non-browser host). Duration is optional.
      done(0);
    }
  });
}

/** One presigned PUT with the same backoff the live chunk queue uses. */
async function putPartWithRetry(url: string, blob: Blob): Promise<string> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= UPLOAD_MAX_RETRIES; attempt++) {
    try {
      return await uploadChunkToS3(url, blob);
    } catch (error) {
      lastError = error;
      if (attempt === UPLOAD_MAX_RETRIES) break;
      const delay = UPLOAD_RETRY_DELAYS_MS[attempt] ?? 4000;
      await new Promise((r) => setTimeout(r, delay));
    }
  }
  throw lastError;
}

/**
 * Run the full lifecycle for `file`.
 *
 * Aborts the multipart upload on any failure. Leaving it open leaks the parts
 * already in the bucket and — worse — leaves the participant with an upload_id
 * the next attempt cannot reuse, so the retry fails for a reason that has
 * nothing to do with the second file.
 */
export interface UploadRecordingFileOptions {
  onProgress?: (progress: FileUploadProgress) => void;
  signal?: AbortSignal;
  /**
   * Overrides the direct-to-S3 PUT.
   *
   * The same seam `RecordingAdapter` already provides for the backend calls,
   * applied to the one request that does not go through it. It keeps the
   * part-numbering and abort-on-failure logic testable without a browser, a
   * network stub, or a copy of axios resolvable from the caller's tree.
   */
  uploadPart?: (url: string, blob: Blob) => Promise<string>;
}

export async function uploadRecordingFile(
  adapter: RecordingAdapter,
  file: File,
  options: UploadRecordingFileOptions = {},
): Promise<UploadRecordingFileResult> {
  const { onProgress, signal, uploadPart = putPartWithRetry } = options;
  const refusal = validateRecordingFile(file);
  if (refusal) throw new Error(refusal);

  const durationSeconds = await readAudioDurationSeconds(file);

  const session = await adapter.start({
    filename: file.name,
    content_type: file.type || 'audio/mpeg',
  });

  const partSize = session.part_size_bytes;
  const totalParts = Math.max(1, Math.ceil(file.size / partSize));
  const presigned = [...session.presigned_urls];
  const parts: UploadPartInfo[] = [];

  try {
    for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
      if (signal?.aborted) throw new Error('Upload cancelled.');

      // The start call presigns only an initial batch; top it up as we go so a
      // long file does not need every URL minted (and expiring) up front.
      if (presigned.length < partNumber) {
        const more = await adapter.moreUrls({
          upload_id: session.upload_id,
          next_part_number: partNumber,
        });
        if (more.presigned_urls.length === 0) {
          throw new Error('Storage stopped issuing upload URLs before the file finished.');
        }
        presigned.push(...more.presigned_urls);
      }

      const entry = presigned.find((u) => u.part_number === partNumber);
      if (!entry) {
        throw new Error(`Missing an upload URL for part ${partNumber}.`);
      }

      const blob = file.slice((partNumber - 1) * partSize, partNumber * partSize);
      const etag = await uploadPart(entry.url, blob);
      parts.push({ part_number: partNumber, etag });

      onProgress?.({
        uploadedParts: parts.length,
        totalParts,
        percent: Math.round((parts.length / totalParts) * 100),
      });
    }

    return await adapter.complete({
      upload_id: session.upload_id,
      parts,
      duration_seconds: durationSeconds,
    });
  } catch (error) {
    // Best-effort: a failed abort must not mask the error that caused it.
    await adapter.abort({ upload_id: session.upload_id }).catch(() => undefined);
    throw error;
  }
}
